import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workspaceEmailAliasContract } from "@common/contracts/workspace-email-alias.contract";
import {
    CreateWorkspaceEmailAliasRequest,
    WorkspaceEmailAlias,
} from "@common/schemas/workspace-email-alias.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const workspaceEmailAliasContractClient = getBackendAPIClient(
    workspaceEmailAliasContract,
);

const workspaceEmailAliasQueryKeys = {
    all: () => ["workspace-email-alias"],
    workspace: (workspaceId: string) => [
        ...workspaceEmailAliasQueryKeys.all(),
        workspaceId,
    ],
} as const;

export function useWorkspaceEmailAliases() {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId
            ? workspaceEmailAliasQueryKeys.workspace(workspaceId)
            : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const result =
                await workspaceEmailAliasContractClient.getForWorkspace({
                    params: {
                        workspaceId,
                    },
                });

            return result.body.emailAliases;
        },
        enabled: !!workspaceId,
    });
}

export function useCreateWorkspaceEmailAlias() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: CreateWorkspaceEmailAliasRequest) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const result = await workspaceEmailAliasContractClient.create({
                params: {
                    workspaceId,
                },
                body,
            });

            return result.body;
        },
        onMutate: async (body) => {
            if (!workspaceId || "customName" in body) {
                return;
            }

            const queryKey =
                workspaceEmailAliasQueryKeys.workspace(workspaceId);

            await queryClient.cancelQueries({ queryKey });

            const previousData = queryClient.getQueryData(queryKey);

            queryClient.setQueryData(
                queryKey,
                (old: WorkspaceEmailAlias[] = []) => [
                    {
                        emailLocalPart: body.emailLocalPart,
                        workspaceId,
                        limit: 500,
                        kind: body.kind ?? "receipts",
                        counterpartyId: body.counterpartyId ?? null,
                        entityUuid: null,
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    },
                    ...old,
                ],
            );

            return { previousData, queryKey };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.queryKey && context?.previousData !== undefined) {
                queryClient.setQueryData(
                    context.queryKey,
                    context.previousData,
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey:
                        workspaceEmailAliasQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useRemoveWorkspaceEmailAlias() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            emailLocalPart,
            kind,
        }: Pick<WorkspaceEmailAlias, "emailLocalPart" | "kind">) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await workspaceEmailAliasContractClient.remove({
                params: {
                    workspaceId,
                    emailLocalPart,
                },
                query: { kind },
            });
        },
        onMutate: async ({ emailLocalPart, kind }) => {
            if (!workspaceId) {
                return;
            }

            const queryKey =
                workspaceEmailAliasQueryKeys.workspace(workspaceId);

            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically remove
            queryClient.setQueryData(
                queryKey,
                (old: WorkspaceEmailAlias[] = []) =>
                    old.filter(
                        (alias) =>
                            alias.emailLocalPart !== emailLocalPart ||
                            alias.kind !== kind,
                    ),
            );

            return { previousData, queryKey };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.queryKey && context?.previousData !== undefined) {
                queryClient.setQueryData(
                    context.queryKey,
                    context.previousData,
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey:
                        workspaceEmailAliasQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}
